<script setup lang="ts">
import ToolbarButton from "@/components/TipTapEditor/ToolbarButton.vue";
import { Editor, NodeViewProps, NodeViewWrapper } from "@tiptap/vue-3";
import "vue-draggable-resizable/dist/VueDraggableResizable.css";
import VueDraggableResizable from "vue-draggable-resizable/src/components/vue-draggable-resizable.vue";

const props = defineProps<{
  editor: typeof Editor;
  node: typeof NodeViewProps;
  selected: boolean;
  updateAttributes: (typeof NodeViewProps)["updateAttributes"];
  deleteNode: (typeof NodeViewProps)["deleteNode"];
}>();

const resizableImg = ref();
const width = ref<number>(props.node?.attrs?.width);
const height = ref<number>(props.node?.attrs?.height);
const lockAspectRatio = ref(false);

const onResize = (_x: number, _y: number, w: number, h: number) => {
  width.value = w;
  height.value = h;
};

const onResizeStop = (_x: number, _y: number, w: number, h: number) => {
  width.value = w;
  height.value = h;

  props.updateAttributes({
    width: w,
    height: h,
  });
};

const maxWidth = computed(() => {
  return Math.max(50, props.editor.view.dom.offsetWidth - 32);
});

const computedStyles = computed(() => {
  return {
    float: props.node.attrs.float,
    width: `${width.value}px`,
    height: `${height.value}px`,
  };
});

onMounted(() => {
  resizableImg.value.onload = () => {
    const aspectRatio =
      resizableImg.value.naturalWidth / resizableImg.value.naturalHeight;

    if (props.node.attrs.height == 0) {
      const minWidth = Math.min(
        maxWidth.value,
        resizableImg.value.naturalWidth
      );
      const matchingHeight = minWidth / aspectRatio;
      width.value = minWidth;
      height.value = matchingHeight;

      props.updateAttributes({
        width: width.value,
        height: height.value,
      });
    }

    nextTick(() => {
      lockAspectRatio.value = true;
    });
  };
});

const caption = computed(() => {
  return props.node.attrs.alt || "";
});

const isImageResizable = ref(false);
const enableResizeMode = () => {
  isImageResizable.value = !isImageResizable.value;
  props.updateAttributes({
    selected: true,
  });
};

watch(
  () => props.selected,
  (newValue) => {
    if (!newValue) {
      isImageResizable.value = false;
    }
  }
);

const imageButtons = [
  {
    action: () =>
      props.editor.chain().focus().setImage({ float: "left" }).run(),
    isActive: () =>
      props.editor.isActive("ResizableImage", {
        float: "left",
      }),
    icon: "bi bi-text-left",
  },
  {
    action: () =>
      props.editor.chain().focus().setImage({ float: "none" }).run(),
    isActive: () =>
      props.editor.isActive("ResizableImage", {
        float: "none",
      }),
    icon: "bi bi-text-center",
  },
  {
    action: () =>
      props.editor.chain().focus().setImage({ float: "right" }).run(),
    isActive: () =>
      props.editor.isActive("ResizableImage", {
        float: "right",
      }),
    icon: "bi bi-text-right",
  },
];

const promptAltText = () => {
  const altText = window.prompt("Enter alt text", props.node.attrs.alt || "");
  if (altText) {
    props.updateAttributes({
      alt: altText,
    });
  }
};

const toggleLink = async () => {
  const url = window.prompt("URL", props.node.attrs.href || "");
  if (url) {
    props.updateAttributes({
      href: url,
    });
  }
};
</script>
<template>
  <NodeViewWrapper
    style="max-width: 100%"
    :style="computedStyles"
    class="resizeable-image-wrapper"
    :class="[
      `resizable-image-float-${node.attrs.float}`,
      { selected: selected },
    ]"
  >
    <template v-if="editor.isEditable">
      <template v-if="isImageResizable">
        <vue-draggable-resizable
          :w="width"
          :h="height"
          :active="selected"
          :min-width="50"
          :max-width="maxWidth"
          :min-height="50"
          :max-height="1000"
          :draggable="false"
          :lock-aspect-ratio="lockAspectRatio"
          :handles="['br', 'tr']"
          @resizing="onResize"
          @resizestop="onResizeStop"
        >
          <figure>
            <img ref="resizableImg" :alt="caption" :src="node.attrs.src" />
          </figure>
        </vue-draggable-resizable>
      </template>
      <figure
        v-else
        data-drag-handle
        class="figure"
        :class="{ 'figure--resizable': isImageResizable }"
      >
        <template v-if="node.attrs.url">
          <a :href="node.attrs.url" target="_blank" rel="noopener noreferrer">
            <img ref="resizableImg" :alt="caption" :src="node.attrs.src" />
          </a>
        </template>
        <template v-else>
          <img ref="resizableImg" :alt="caption" :src="node.attrs.src" />
        </template>
        <figcaption>{{ caption }}</figcaption>
        <div class="figure__actions">
          <div class="figure__toolbar">
            <ToolbarButton
              v-for="button in imageButtons"
              :key="button.icon"
              :class="{
                'is-active': button.isActive ? button.isActive() : null,
              }"
              @click.prevent="button.action"
            >
              <i :class="button.icon"></i>
            </ToolbarButton>
            <ToolbarButton class="ms-2" @click.prevent="promptAltText">
              <i class="bi bi-type"></i>
            </ToolbarButton>
            <ToolbarButton @click.prevent="toggleLink">
              <i class="bi bi-link-45deg"></i>
            </ToolbarButton>
            <ToolbarButton @click.prevent.stop="enableResizeMode">
              <i class="bi bi-bounding-box"></i>
            </ToolbarButton>
          </div>
        </div>
      </figure>
    </template>
    <template v-else>
      <figure class="figure">
        <template v-if="node.attrs.url">
          <a :href="node.attrs.url" target="_blank" rel="noopener noreferrer">
            <img ref="resizableImg" :alt="caption" :src="node.attrs.src" />
          </a>
        </template>
        <template v-else>
          <img ref="resizableImg" :alt="caption" :src="node.attrs.src" />
        </template>
        <figcaption>{{ caption }}</figcaption>
      </figure>
    </template>
  </NodeViewWrapper>
</template>

<style scoped lang="scss">
.figure {
  position: relative;
  cursor: move;

  &__actions {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    width: 100%;
    display: flex;
    justify-content: center;
  }

  &__toolbar {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: $spacer-1;
    background: $color-white;
    box-shadow: $box-shadow;
    border-radius: $border-radius;
    padding: $spacer-1 $spacer-1;
    opacity: 0;
    transition: opacity 0.2s ease-in-out;

    button {
      font-size: 1.1rem;
    }
  }

  &:hover &__toolbar {
    opacity: 1 !important;
  }

  &--resizable {
    cursor: inherit;
  }
}
</style>

<style lang="scss">
.resizeable-image-wrapper {
  outline: transparent 3px solid;
  margin: 0.9em 0;
  transition: outline-color 0.2s ease;

  &.selected {
    outline-color: $primary-bright;
  }
}

.vdr {
  border: none;
  max-width: calc(100% - 26px);

  &:hover {
    outline: 3px solid $primary-light;
  }

  &.active {
    outline: 3px solid $primary-light;
  }
}

.handle {
  border: 1px solid $primary;
  background-color: $primary;
}

.handle-br {
  bottom: -6px;
  right: -6px;
}
</style>
